import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { FAQS } from "../lib/faqs";
import { useSeo } from "../lib/seo";
import { Search, Car, Newspaper, LifeBuoy } from "lucide-react";

export default function SearchPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [vehicles, setVehicles] = useState([]);
  const [posts, setPosts] = useState([]);
  useSeo({ title: `Search — ${q || "Europcar UAE"}`, description: "Search vehicles, articles and support answers across Europcar Dubai & Northern Emirates." });

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.posts)).catch(() => {});
  }, []);
  useEffect(() => setInput(q), [q]);

  const term = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!term) return { cars: [], articles: [], faqs: [] };
    return {
      cars: vehicles.filter((v) => `${v.name} ${v.group} ${v.group_label} ${v.acriss} ${v.fuel}`.toLowerCase().includes(term)).slice(0, 8),
      articles: posts.filter((p) => `${p.title} ${p.excerpt} ${p.category}`.toLowerCase().includes(term)).slice(0, 6),
      faqs: FAQS.filter((f) => `${f.q} ${f.a}`.toLowerCase().includes(term)).slice(0, 8),
    };
  }, [term, vehicles, posts]);

  const total = results.cars.length + results.articles.length + results.faqs.length;

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <h1 className="heading-upper text-2xl text-white promo-stripe" data-testid="search-heading">{t("Find it fast.")}</h1>
          <form className="mt-5 flex gap-2" onSubmit={(e) => { e.preventDefault(); setParams(input.trim() ? { q: input.trim() } : {}); }}>
            <input className="field-input flex-1" placeholder={t("Try 'Tucson', 'deposit', 'delivery'…")} value={input} onChange={(e) => setInput(e.target.value)} autoFocus data-testid="search-input" />
            <button type="submit" className="bg-[#FFDC00] text-[#0F1E17] font-bold rounded px-5 text-xs flex items-center gap-2" data-testid="search-submit"><Search className="w-4 h-4" />{t("Search")}</button>
          </form>
        </div>
      </div>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full flex-1 space-y-8">
        {term && <p className="text-xs text-[#8a978f]" data-testid="search-summary">{t("{{n}} results for \"{{q}}\"", { n: total, q })}</p>}
        {results.cars.length > 0 && (
          <section data-testid="search-vehicles">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2 mb-3"><Car className="w-4 h-4 text-[#009900]" /> {t("Vehicles")}</h2>
            <div className="divide-y divide-[#f1f3f1] bg-white border border-[#e2ebe4] rounded-lg">
              {results.cars.map((v) => (
                <Link key={v.id} to="/fleet" className="flex items-center justify-between gap-3 p-3 hover:bg-[#fafcfa]" data-testid={`search-vehicle-${v.id}`}>
                  <span className="text-sm text-[#0F1E17] font-medium">{v.name} <span className="text-[10px] text-[#8a978f]">{t("Group")} {v.group} · {v.group_label}</span></span>
                  <span className="text-xs font-bold text-[#144733]">{t("From AED {{amt}}/day incl. VAT", { amt: v.daily_incl })}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
        {results.articles.length > 0 && (
          <section data-testid="search-articles">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2 mb-3"><Newspaper className="w-4 h-4 text-[#009900]" /> {t("Articles")}</h2>
            <div className="divide-y divide-[#f1f3f1] bg-white border border-[#e2ebe4] rounded-lg">
              {results.articles.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="block p-3 hover:bg-[#fafcfa]" data-testid={`search-article-${p.slug}`}>
                  <span className="text-sm text-[#0F1E17] font-medium">{p.title}</span>
                  <p className="text-xs text-[#57685e] mt-0.5 line-clamp-1">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
        {results.faqs.length > 0 && (
          <section data-testid="search-support">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2 mb-3"><LifeBuoy className="w-4 h-4 text-[#009900]" /> {t("Support answers")}</h2>
            <div className="divide-y divide-[#f1f3f1] bg-white border border-[#e2ebe4] rounded-lg">
              {results.faqs.map((f) => (
                <details key={f.q} className="p-3">
                  <summary className="text-sm text-[#0F1E17] font-medium cursor-pointer">{t(f.q)}</summary>
                  <p className="text-xs text-[#57685e] mt-2 leading-relaxed">{t(f.a)}</p>
                </details>
              ))}
            </div>
          </section>
        )}
        {term && total === 0 && (
          <div className="text-center py-12 text-sm text-[#57685e]" data-testid="search-empty">
            {t("Nothing matched — try a model name, or browse the")} <Link to="/fleet" className="text-[#009900] underline">{t("full fleet")}</Link> {t("and")} <Link to="/help" className="text-[#009900] underline">{t("help centre")}</Link>.
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
